import { type ReactNode, useEffect, useState } from 'react';

import Button, { type ButtonVariant } from 'components/Button';
import RichText from 'components/RichText';
import Text from 'components/Text';
import { cn } from 'utils/classNames';
import { useI18n } from 'utils/i18n';

interface Props {
	/** Content after the description text */
	afterDescription?: ReactNode;

	/** Content before the description text */
	beforeDescription?: ReactNode;

	/** Button placement */
	buttonPlacement?: 'left' | 'middle' | 'right';

	/** Button variant */
	buttonVariant?: ButtonVariant;

	/** Container class name */
	className?: string;

	/** The description text */
	description: string | undefined;

	/** Description text class name */
	descriptionClassName?: string;

	/** Description heading */
	heading?: string;

	/** Should it be open by default? */
	isExpanded?: boolean;

	/** If the heading should be visible, otherwise just for screen readers */
	showHeading?: boolean;

	/** Hidden height */
	textHeight?: 'short' | 'tall';

	/** Character limit to display the expand button */
	textLengthBreakPoint: number;
}

/** Text content with a max height, expandable with a 'read more' button. */
export default function ExpandableDescription({
	afterDescription,
	beforeDescription,
	buttonPlacement = 'middle',
	buttonVariant = 'branded',
	className,
	description,
	descriptionClassName,
	heading,
	isExpanded: isExpandedProp = false,
	showHeading = false,
	textHeight = 'tall',
	textLengthBreakPoint,
}: Props) {
	const { t } = useI18n();
	const textLength = description?.length ?? 0;
	const isLessThanBreakPoint = textLength < textLengthBreakPoint;
	const [isExpanded, setIsExpanded] = useState(
		isExpandedProp || isLessThanBreakPoint,
	);

	useEffect(() => {
		if (!isExpanded && isLessThanBreakPoint) {
			setIsExpanded(true);
		}
	}, [description, textLengthBreakPoint, isExpanded, isLessThanBreakPoint]);

	const handleExpandClick = () => {
		setIsExpanded(true);
	};

	return (
		<div className={className}>
			{heading && showHeading && (
				<Text as="h2" className="mb-2">
					{heading}
				</Text>
			)}
			<div
				className={cn(
					'relative overflow-hidden',
					// When collapsed, the expand button area has bottom spacing.
					// Add some when expanded to match the look.
					isExpanded && 'mb-6',
					!isExpanded && [
						'min-h-32',
						textHeight === 'short' && 'max-h-60',
						textHeight === 'tall' && 'max-h-80 sm:max-h-100',
					],
				)}
			>
				{beforeDescription}
				{description && (
					<RichText className={descriptionClassName} html={description} />
				)}
				{afterDescription}

				{!isExpanded && (
					<div
						className={cn(
							'absolute right-0 bottom-0 left-0 flex h-34 items-end bg-linear-to-t from-base to-transparent pb-6 forced-color-adjust-none forced-colors:from-[Canvas]',
							buttonPlacement === 'left' && 'justify-start',
							buttonPlacement === 'middle' && 'justify-center',
							buttonPlacement === 'right' && 'justify-end',
							buttonVariant === 'text' ? 'from-50%' : 'from-65%',
						)}
					>
						<Button
							variant={buttonVariant}
							onClick={handleExpandClick}
							className="forced-color-adjust-auto"
						>
							{t('product_details_description_read_more_button')}
							<Text as="screenReader">, {heading}</Text>
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}
ExpandableDescription.displayName = 'ExpandableDescription';
